import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Activity, ChevronLeft, ChevronRight, Filter, Search, User, Clock, ArrowUpDown, X } from 'lucide-react';
import api from '../services/api';

const ACTION_LABELS = {
  pricelist_create: 'Utworzenie', pricelist_update: 'Edycja', pricelist_delete: 'Usunięcie', pricelist_import: 'Import',
  pricelist_item_create: 'Dodanie pozycji', pricelist_item_update: 'Edycja pozycji', pricelist_item_delete: 'Usunięcie pozycji',
  pricelist_item_copy: 'Kopiowanie pozycji',
  offer_create: 'Utworzenie oferty', offer_update: 'Edycja oferty', offer_delete: 'Usunięcie oferty',
  message_send: 'Wysłanie wiadomości', message_draft: 'Zapisanie szkicu',
  user_login: 'Logowanie', user_archive: 'Archiwizacja', user_restore: 'Przywrócenie', user_role_change: 'Zmiana roli',
  role_create: 'Utworzenie roli', role_edit: 'Edycja roli', role_delete: 'Usunięcie roli',
  system_config_update: 'Zmiana konfiguracji',
};

const ENTITY_LABELS = {
  pricelist: 'Cennik', pricelist_item: 'Pozycja cennika',
  offer: 'Oferta', offer_item: 'Pozycja oferty',
  message: 'Wiadomość',
  user: 'Użytkownik', role: 'Rola', system_config: 'Konfiguracja',
};

const getActionColor = (action) => {
  if (action.includes('create') || action.includes('import')) return 'bg-green-100 text-green-800';
  if (action.includes('edit') || action.includes('update')) return 'bg-blue-100 text-blue-800';
  if (action.includes('delete')) return 'bg-red-100 text-red-800';
  if (action.includes('archive')) return 'bg-yellow-100 text-yellow-800';
  if (action.includes('restore')) return 'bg-purple-100 text-purple-800';
  if (action.includes('copy')) return 'bg-indigo-100 text-indigo-800';
  if (action.includes('send')) return 'bg-cyan-100 text-cyan-800';
  if (action.includes('login')) return 'bg-gray-100 text-gray-800';
  if (action.includes('role')) return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-800';
};

const renderDetails = (details) => {
  if (!details) return null;
  const d = typeof details === 'string' ? JSON.parse(details) : details;

  if (d.zmiany && typeof d.zmiany === 'object') {
    const entries = Object.entries(d.zmiany);
    if (entries.length === 0) return <span className="text-gray-400 italic">Brak zmian</span>;
    return (
      <div className="space-y-0.5">
        {entries.map(([field, val]) => (
          <div key={field} className="flex flex-wrap items-center gap-1">
            <span className="font-medium text-gray-600">{field}:</span>
            {val && typeof val === 'object' && 'przed' in val ? (
              <>
                <span className="line-through text-red-500 text-xs">{String(val.przed || '-')}</span>
                <span className="text-gray-400">&rarr;</span>
                <span className="text-green-600 text-xs font-medium">{String(val.po || '-')}</span>
              </>
            ) : (
              <span className="text-gray-700 text-xs">{String(val)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  const entries = Object.entries(d).filter(([k]) => k !== 'ip' && k !== 'user_agent');
  if (entries.length === 0) return null;
  return (
    <div className="space-y-0.5">
      {entries.map(([key, val]) => (
        <div key={key}>
          <span className="font-medium text-gray-600">{key}: </span>
          <span className="text-gray-700 text-xs">
            {typeof val === 'object' && val !== null
              ? Object.entries(val).map(([k, v]) => `${k}: ${v ?? '-'}`).join(', ')
              : String(val)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function ActivityLogsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(30);
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get('/users').then(res => setUsers(res.data.users || [])).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filterAction) params.action = filterAction;
      if (filterEntityType) params.entity_type = filterEntityType;
      if (filterUserId) params.user_id = filterUserId;

      const res = await api.get('/system/activity-logs', { params });
      let fetched = res.data.logs || [];

      if (sortOrder === 'asc') fetched = [...fetched].reverse();
      if (searchText) {
        const s = searchText.toLowerCase();
        fetched = fetched.filter(l =>
          (l.entity_name && l.entity_name.toLowerCase().includes(s)) ||
          (l.user_name && l.user_name.toLowerCase().includes(s)) ||
          (l.action && l.action.toLowerCase().includes(s))
        );
      }

      setLogs(fetched);
      setTotal(res.data.total || 0);
    } catch { /* */ } finally { setLoading(false); }
  }, [page, limit, filterAction, filterEntityType, filterUserId, searchText, sortOrder]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  if (user?.role !== 'admin') return <Navigate to="/" />;

  const totalPages = Math.ceil(total / limit);
  const formatDate = (d) => new Date(d).toLocaleDateString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
  const clearFilters = () => { setFilterAction(''); setFilterEntityType(''); setFilterUserId(''); setSearchText(''); setPage(1); };
  const hasFilters = filterAction || filterEntityType || filterUserId || searchText;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Activity size={24} className="text-blue-600" /> {t('activityLogs.title')}
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1">
            <ArrowUpDown size={16} /> {sortOrder === 'desc' ? t('activityLogs.newest') : t('activityLogs.oldest')}
          </button>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg text-sm flex items-center gap-1 ${showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 hover:bg-gray-200'}`}>
            <Filter size={16} /> {t('activityLogs.filters')}
            {hasFilters && <span className="w-2 h-2 bg-blue-600 rounded-full" />}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs text-gray-500 mb-1">{t('common.search')}</label>
            <div className="relative">
              <Search size={16} className="absolute left-2 top-2.5 text-gray-400" />
              <input type="text" value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }}
                placeholder={t('activityLogs.searchPlaceholder')} className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm" />
            </div>
          </div>
          <div className="min-w-[130px]">
            <label className="block text-xs text-gray-500 mb-1">{t('activityLogs.action')}</label>
            <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded-lg text-sm">
              <option value="">{t('activityLogs.all')}</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="min-w-[130px]">
            <label className="block text-xs text-gray-500 mb-1">{t('activityLogs.type')}</label>
            <select value={filterEntityType} onChange={e => { setFilterEntityType(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded-lg text-sm">
              <option value="">{t('activityLogs.all')}</option>
              {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="min-w-[130px]">
            <label className="block text-xs text-gray-500 mb-1">{t('activityLogs.user')}</label>
            <select value={filterUserId} onChange={e => { setFilterUserId(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded-lg text-sm">
              <option value="">{t('activityLogs.allUsers')}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title={t('activityLogs.clear')}>
              <X size={18} />
            </button>
          )}
        </div>
      )}

      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">{t('activityLogs.noEntries')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">{t('activityLogs.date')}</th>
                  <th className="px-3 py-2 text-left">{t('activityLogs.user')}</th>
                  <th className="px-3 py-2 text-left">{t('activityLogs.action')}</th>
                  <th className="px-3 py-2 text-left">{t('activityLogs.type')}</th>
                  <th className="px-3 py-2 text-left">{t('common.name')}</th>
                  <th className="px-3 py-2 text-left hidden lg:table-cell">{t('activityLogs.details')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      <div className="flex items-center gap-1"><Clock size={14} className="text-gray-400" />
                        <span className="text-xs">{formatDate(log.created_at)}</span></div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1"><User size={14} className="text-gray-400" />
                        <span className="font-medium text-gray-700">{log.user_name || '-'}</span></div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{ENTITY_LABELS[log.entity_type] || log.entity_type}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 max-w-[200px] truncate">{log.entity_name || '-'}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs hidden lg:table-cell max-w-[400px]">
                      {log.details ? renderDetails(log.details) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <div className="text-sm text-gray-500">{t('activityLogs.pageInfo', { page, totalPages, total })}</div>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded border bg-white hover:bg-gray-50 disabled:opacity-30"><ChevronLeft size={16} /></button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let n;
                if (totalPages <= 5) n = i + 1;
                else if (page <= 3) n = i + 1;
                else if (page >= totalPages - 2) n = totalPages - 4 + i;
                else n = page - 2 + i;
                return (
                  <button key={n} onClick={() => setPage(n)}
                    className={`px-3 py-1 rounded border text-sm ${page === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50'}`}>{n}</button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded border bg-white hover:bg-gray-50 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
